import { useState, useRef, useEffect } from 'react'
import Card from '../ui/Card'
import { Input } from '../ui'
import type { Vendor } from '../../lib/services'
import { vendorsAPI } from '../../lib/services'
import { Plus, X, Info } from 'lucide-react'

interface BasicProfileCardProps {
  vendor: Vendor
  onRefresh: () => void
}

interface ProfileField {
  key: keyof Vendor
  label: string
  type?: 'text' | 'url' | 'email' | 'tel' | 'number' | 'textarea'
}

const PROFILE_FIELDS: ProfileField[] = [
  { key: 'name', label: 'Company Name' },
  { key: 'website', label: 'Website', type: 'url' },
  { key: 'headquarters_location', label: 'Headquarters' },
  { key: 'address', label: 'Address' },
  { key: 'uei', label: 'UEI (SAM.gov)' },
  { key: 'cage_code', label: 'CAGE Code' },
  { key: 'duns_number', label: 'DUNS Number' },
  { key: 'founded_year', label: 'Founded Year', type: 'number' },
  { key: 'employee_count', label: 'Employee Count', type: 'number' },
  { key: 'entity_structure', label: 'Entity Structure' },
  { key: 'annual_revenue_range', label: 'Revenue Range' },
  { key: 'primary_naics', label: 'Primary NAICS' },
]

function InlineEditField({ field, value, vendorId, onSaved }: {
  field: ProfileField
  value: any
  vendorId: number
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [editing])

  const startEditing = () => {
    setEditValue(value != null ? String(value) : '')
    setEditing(true)
  }

  const save = async () => {
    if (editValue === (value != null ? String(value) : '')) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      let patchValue: any = editValue
      if (field.type === 'number' && editValue !== '') patchValue = Number(editValue)
      await vendorsAPI.update(vendorId, { [field.key]: patchValue } as any)
      onSaved()
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const cancel = () => {
    setEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && field.type !== 'textarea') save()
    if (e.key === 'Escape') cancel()
  }

  const displayValue = value != null && value !== '' ? String(value) : null

  if (editing) {
    return (
      <div className="flex-1 min-w-0">
        {field.type === 'textarea' ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === 'Escape') cancel() }}
            className="w-full px-2 py-1.5 text-sm border border-primary/40 rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            rows={3}
            disabled={saving}
          />
        ) : (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            type={field.type || 'text'}
            className="h-7 text-sm"
            disabled={saving}
          />
        )}
      </div>
    )
  }

  return (
    <button
      onClick={startEditing}
      className="flex-1 min-w-0 text-left group/field"
    >
      {displayValue ? (
        <span className="text-sm text-foreground group-hover/field:text-primary transition-colors">
          {displayValue}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground/50 italic group-hover/field:text-primary transition-colors">
          Click to add
        </span>
      )}
    </button>
  )
}

function TagListField({ label, values, vendorId, fieldKey, onSaved, placeholder }: {
  label: string
  values: any[] | null | undefined
  vendorId: number
  fieldKey: keyof Vendor
  onSaved: () => void
  placeholder?: string
}) {
  const [adding, setAdding] = useState(false)
  const [newValue, setNewValue] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus()
  }, [adding])

  const items: string[] = Array.isArray(values)
    ? values.map(v => (typeof v === 'object' && v !== null ? (v.code || v.description || JSON.stringify(v)) : String(v)))
    : []

  const addItem = async () => {
    const trimmed = newValue.trim()
    if (!trimmed || items.includes(trimmed)) {
      setAdding(false)
      setNewValue('')
      return
    }
    setSaving(true)
    try {
      await vendorsAPI.update(vendorId, { [fieldKey]: [...items, trimmed] } as any)
      onSaved()
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setSaving(false)
      setAdding(false)
      setNewValue('')
    }
  }

  const removeItem = async (item: string) => {
    setSaving(true)
    try {
      await vendorsAPI.update(vendorId, { [fieldKey]: items.filter(i => i !== item) } as any)
      onSaved()
    } catch (err) {
      console.error('Failed to save:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-start gap-3">
      <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1 select-none">{label}</span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5">
          {items.map(item => (
            <span key={item} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-muted text-foreground group/tag">
              {item}
              <button
                onClick={() => removeItem(item)}
                disabled={saving}
                className="text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover/tag:opacity-100"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {adding ? (
            <input
              ref={inputRef}
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onBlur={addItem}
              onKeyDown={e => {
                if (e.key === 'Enter') addItem()
                if (e.key === 'Escape') { setAdding(false); setNewValue('') }
              }}
              disabled={saving}
              placeholder={placeholder || 'Add...'}
              className="h-6 w-24 px-1.5 text-xs border border-primary/40 rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded-md border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const DEFAULT_VISIBLE = 5

export default function BasicProfileCard({ vendor, onRefresh }: BasicProfileCardProps) {
  const [expanded, setExpanded] = useState(false)

  const filledFields = PROFILE_FIELDS.filter(f => {
    const val = vendor[f.key]
    return val !== null && val !== undefined && val !== ''
  })
  const completeness = Math.round((filledFields.length / PROFILE_FIELDS.length) * 100)

  const displayFields = expanded ? PROFILE_FIELDS : PROFILE_FIELDS.slice(0, DEFAULT_VISIBLE)
  const hiddenCount = PROFILE_FIELDS.length - DEFAULT_VISIBLE

  return (
    <Card className="p-6">
      <div className="mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <h3 className="text-base font-semibold text-foreground">Basic Profile</h3>
            <div className="relative group/tip">
              <Info className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 px-3 py-2 text-xs text-foreground bg-popover border border-border rounded-lg shadow-lg opacity-0 pointer-events-none group-hover/tip:opacity-100 group-hover/tip:pointer-events-auto transition-opacity z-50">
                Details are inferred from public databases, web research, and uploaded documents. Click any field to edit, or upload documents at the bottom of this page to auto-fill details.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${completeness}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{completeness}%</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {displayFields.map(f => (
          <div key={f.key} className="flex items-start gap-3">
            <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1 select-none">
              {f.label}
            </span>
            <InlineEditField
              field={f}
              value={vendor[f.key]}
              vendorId={vendor.id}
              onSaved={onRefresh}
            />
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-3 mt-4 space-y-3">
        <TagListField
          label="NAICS Codes"
          values={vendor.naics_codes}
          vendorId={vendor.id}
          fieldKey="naics_codes"
          onSaved={onRefresh}
          placeholder="e.g. 541512"
        />
        <TagListField
          label="PSC Codes"
          values={vendor.psc_codes}
          vendorId={vendor.id}
          fieldKey="psc_codes"
          onSaved={onRefresh}
          placeholder="e.g. D310"
        />
        <TagListField
          label="Consortiums"
          values={vendor.consortium_memberships}
          vendorId={vendor.id}
          fieldKey="consortium_memberships"
          onSaved={onRefresh}
          placeholder="e.g. NSTXL"
        />
      </div>

      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-500 hover:text-blue-600 mt-3"
        >
          {expanded ? 'Show less' : `... ${hiddenCount} more`}
        </button>
      )}
    </Card>
  )
}
